import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  MessageCircle,
  Send,
  Paperclip,
  Camera,
  Settings2,
  ShieldAlert,
  X,
  ArrowLeft,
  Copy,
  RefreshCw,
  Loader2,
  Check,
  CheckCheck,
  Search,
  Phone,
} from "lucide-react";

const backendBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
const mediaSrc = (u) => (u ? `${backendBase}${u}` : "");
const IST_TIME = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Asia/Kolkata",
    });
  } catch {
    return "";
  }
};
const IST_DAY = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      timeZone: "Asia/Kolkata",
    });
  } catch {
    return "";
  }
};

const AVATAR_COLORS = [
  "bg-emerald-500",
  "bg-teal-500",
  "bg-cyan-500",
  "bg-sky-500",
  "bg-indigo-500",
  "bg-fuchsia-500",
  "bg-pink-500",
  "bg-rose-500",
];
const avatarColor = (id = "") => {
  let n = 0;
  for (const c of id) n = (n + c.charCodeAt(0)) % AVATAR_COLORS.length;
  return AVATAR_COLORS[n];
};
const initials = (name = "?") =>
  name
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((s) => s[0] || "")
    .join("")
    .toUpperCase();

function StatusIcon({ status }) {
  if (status === "read")
    return <CheckCheck className="w-3.5 h-3.5 text-sky-500" />;
  if (status === "delivered")
    return <CheckCheck className="w-3.5 h-3.5 text-slate-400" />;
  if (status === "sent")
    return <Check className="w-3.5 h-3.5 text-slate-400" />;
  if (status === "failed")
    return <span className="text-red-500 text-[10px]">!</span>;
  return null;
}

function Bubble({ msg, mine }) {
  const bg = mine ? "bg-[#DCF8C6]" : "bg-white";
  const isImage = msg.kind === "image";
  const isVideo = msg.kind === "video";
  const isDoc = msg.kind === "document";
  const isAudio = msg.kind === "audio" || msg.kind === "voice";
  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"} mb-1`}>
      <div
        className={`max-w-[75%] rounded-lg ${bg} shadow-sm px-2.5 py-1.5`}
        data-testid={`wa-msg-${msg.id}`}
      >
        {msg.media_url && (
          <div className="mb-1 rounded overflow-hidden">
            {isImage && (
              <img
                src={mediaSrc(msg.media_url)}
                alt={msg.media_name || "image"}
                className="max-h-[300px] w-auto rounded cursor-pointer"
                onClick={() => window.open(mediaSrc(msg.media_url), "_blank")}
              />
            )}
            {isVideo && (
              <video
                src={mediaSrc(msg.media_url)}
                controls
                className="max-h-[300px] w-full rounded bg-black"
                preload="metadata"
              />
            )}
            {isAudio && (
              <audio
                src={mediaSrc(msg.media_url)}
                controls
                className="w-full max-w-[280px]"
                preload="metadata"
              />
            )}
            {isDoc && (
              <a
                href={mediaSrc(msg.media_url)}
                target="_blank"
                rel="noreferrer"
                className="flex items-center gap-2 p-2 bg-slate-50 rounded text-slate-700 text-xs"
              >
                <Paperclip className="w-4 h-4" />
                {msg.media_name || "Document"}
              </a>
            )}
          </div>
        )}
        {msg.content && (
          <div className="text-[14px] leading-snug whitespace-pre-wrap break-words text-slate-900">
            {msg.content}
          </div>
        )}
        <div className="flex justify-end items-center gap-1 mt-0.5">
          <span className="mono text-[9px] text-slate-500">
            {IST_TIME(msg.created_at)}
          </span>
          {mine && <StatusIcon status={msg.status} />}
        </div>
      </div>
    </div>
  );
}

function DateChip({ day }) {
  return (
    <div className="flex justify-center my-3">
      <span className="mono text-[10px] uppercase tracking-widest text-slate-700 bg-white/80 border border-slate-200 rounded-full px-3 py-0.5">
        {day}
      </span>
    </div>
  );
}

// ---------- WhatsApp Setup Dialog (owner-only) --------------------------------
function SetupDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({
    waba_id: "",
    phone_number_id: "",
    access_token: "",
    app_secret: "",
    display_number: "",
    verify_token: "",
    member_ids: [],
  });
  const [webhookUrl, setWebhookUrl] = useState("");
  const [maskedAccess, setMaskedAccess] = useState("");
  const [maskedSecret, setMaskedSecret] = useState("");
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get("/whatsapp/config");
        setWebhookUrl(data.webhook_url || "");
        setForm({
          waba_id: data.waba_id || "",
          phone_number_id: data.phone_number_id || "",
          access_token: "",
          app_secret: "",
          display_number: data.display_number || "",
          verify_token: data.verify_token || "",
          member_ids: data.member_ids || [],
        });
        setMaskedAccess(data.access_token_masked || "");
        setMaskedSecret(data.app_secret_masked || "");
        setEmployees(data.employees || []);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Could not load config");
      } finally {
        setLoading(false);
      }
    })();
  }, [open]);

  const copy = (t) => {
    navigator.clipboard.writeText(t);
    toast.success("Copied");
  };

  const toggleMember = (id) =>
    setForm((f) => ({
      ...f,
      member_ids: f.member_ids.includes(id)
        ? f.member_ids.filter((x) => x !== id)
        : [...f.member_ids, id],
    }));

  const save = async () => {
    if (!form.waba_id || !form.phone_number_id) {
      toast.error("WABA ID and Phone Number ID are required.");
      return;
    }
    if (!maskedAccess && !form.access_token) {
      toast.error("Paste your permanent Access Token.");
      return;
    }
    if (!maskedSecret && !form.app_secret) {
      toast.error("Paste your App Secret.");
      return;
    }
    setLoading(true);
    try {
      const body = { ...form };
      // Don't wipe secrets on re-save if user left them blank.
      if (!body.access_token) delete body.access_token;
      if (!body.app_secret) delete body.app_secret;
      // But server requires them on first save — allow blank if already stored.
      if (!body.access_token && maskedAccess) body.access_token = "•"; // placeholder — server keeps old
      if (!body.app_secret && maskedSecret) body.app_secret = "•";
      await api.put("/whatsapp/config", body);
      toast.success("WhatsApp configured");
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setLoading(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/whatsapp/config/test");
      toast.success(
        `Connected as ${data.verified_name || "WhatsApp"} · ${data.display_phone_number || ""}`
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    } finally {
      setTesting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="sm:max-w-lg max-h-[85vh] overflow-y-auto"
        data-testid="wa-setup-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-heading">WhatsApp Business setup</DialogTitle>
          <DialogDescription>
            Paste your Meta Cloud API credentials from Meta Business Manager →
            System User (permanent token). All fields are stored per-tenant.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="text-center py-4 text-slate-500 text-sm">
            <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…
          </div>
        )}

        <div className="space-y-3">
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3 space-y-2">
            <div className="mono text-[10px] uppercase tracking-widest text-slate-500">
              Webhook — set this in Meta → WhatsApp → Configuration
            </div>
            <div className="flex gap-2 items-center">
              <Input
                readOnly
                value={webhookUrl}
                className="mono text-xs bg-white"
                data-testid="wa-webhook-url"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => copy(webhookUrl)}
                data-testid="wa-copy-webhook"
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <div className="mono text-[10px] uppercase tracking-widest text-slate-500 pt-2">
              Verify token — paste in Meta&apos;s webhook config
            </div>
            <div className="flex gap-2 items-center">
              <Input
                value={form.verify_token}
                onChange={(e) =>
                  setForm((f) => ({ ...f, verify_token: e.target.value }))
                }
                className="mono text-xs bg-white"
                placeholder="Auto-generated on save"
                data-testid="wa-verify-token"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  setForm((f) => ({
                    ...f,
                    verify_token: Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2),
                  }))
                }
                title="Regenerate"
              >
                <RefreshCw className="w-4 h-4" />
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => copy(form.verify_token)}
              >
                <Copy className="w-4 h-4" />
              </Button>
            </div>
            <div className="text-[11px] text-slate-500 pt-1">
              Subscribe to the <b>messages</b> field only. Set the callback URL
              and verify token above, then click &quot;Verify and Save&quot; in Meta.
            </div>
          </div>

          <label className="block space-y-1">
            <div className="mono text-[10px] uppercase tracking-widest text-slate-500">
              WABA ID
            </div>
            <Input
              value={form.waba_id}
              onChange={(e) => setForm((f) => ({ ...f, waba_id: e.target.value }))}
              placeholder="WhatsApp Business Account ID"
              data-testid="wa-waba-id"
            />
          </label>
          <label className="block space-y-1">
            <div className="mono text-[10px] uppercase tracking-widest text-slate-500">
              Phone Number ID
            </div>
            <Input
              value={form.phone_number_id}
              onChange={(e) =>
                setForm((f) => ({ ...f, phone_number_id: e.target.value }))
              }
              placeholder="From: your business number"
              data-testid="wa-phone-id"
            />
          </label>
          <label className="block space-y-1">
            <div className="mono text-[10px] uppercase tracking-widest text-slate-500">
              Display phone number (optional, for UI)
            </div>
            <Input
              value={form.display_number}
              onChange={(e) =>
                setForm((f) => ({ ...f, display_number: e.target.value }))
              }
              placeholder="+91..."
              data-testid="wa-display-number"
            />
          </label>
          <label className="block space-y-1">
            <div className="mono text-[10px] uppercase tracking-widest text-slate-500">
              Access token {maskedAccess && <span className="text-slate-400 normal-case">(current: {maskedAccess})</span>}
            </div>
            <Input
              type="password"
              value={form.access_token}
              onChange={(e) =>
                setForm((f) => ({ ...f, access_token: e.target.value }))
              }
              placeholder={maskedAccess ? "Leave blank to keep current" : "Permanent System User token"}
              data-testid="wa-access-token"
            />
          </label>
          <label className="block space-y-1">
            <div className="mono text-[10px] uppercase tracking-widest text-slate-500">
              App secret {maskedSecret && <span className="text-slate-400 normal-case">(current: {maskedSecret})</span>}
            </div>
            <Input
              type="password"
              value={form.app_secret}
              onChange={(e) =>
                setForm((f) => ({ ...f, app_secret: e.target.value }))
              }
              placeholder={maskedSecret ? "Leave blank to keep current" : "From Meta App → Settings → Basic"}
              data-testid="wa-app-secret"
            />
          </label>

          <div className="pt-2 space-y-2">
            <div className="mono text-[10px] uppercase tracking-widest text-slate-500">
              Employees allowed to use WhatsApp
            </div>
            {employees.length === 0 ? (
              <div className="text-[11px] text-slate-500">
                No employees yet.
              </div>
            ) : (
              <div className="space-y-1 max-h-40 overflow-y-auto">
                {employees.map((e) => {
                  const checked = form.member_ids.includes(e.id);
                  return (
                    <label
                      key={e.id}
                      className={
                        "flex items-center gap-2 p-1.5 rounded border cursor-pointer text-sm " +
                        (checked
                          ? "border-[#25D366] bg-emerald-50/40"
                          : "border-slate-200 hover:bg-slate-50")
                      }
                      data-testid={`wa-member-${e.id}`}
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleMember(e.id)}
                      />
                      <span className="flex-1 truncate">{e.name}</span>
                      <span className="mono text-[10px] text-slate-500">
                        {e.department}
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        <DialogFooter className="gap-2">
          {webhookUrl && (
            <Button
              variant="outline"
              onClick={test}
              disabled={testing || loading}
              data-testid="wa-test"
            >
              {testing ? "Testing…" : "Test connection"}
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
            Cancel
          </Button>
          <Button
            onClick={save}
            disabled={loading}
            className="bg-[#25D366] hover:bg-[#128C7E]"
            data-testid="wa-save"
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Main WhatsApp view -----------------------------------------------
export default function WhatsAppInbox() {
  const { user } = useAuth();
  const [access, setAccess] = useState(null);
  const [threads, setThreads] = useState([]);
  const [selected, setSelected] = useState(null); // customer_wa_number
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [sending, setSending] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [newNumberOpen, setNewNumberOpen] = useState(false);
  const [newNumber, setNewNumber] = useState("");
  const listRef = useRef(null);
  const pollRef = useRef(null);
  const fileInputRef = useRef(null);

  const isOwner = user?.role === "owner";

  const loadAccess = useCallback(async () => {
    try {
      const { data } = await api.get("/whatsapp/access");
      setAccess(data);
    } catch {
      setAccess({ has_access: false });
    }
  }, []);

  const loadThreads = useCallback(async () => {
    if (!access?.has_access) return;
    try {
      const { data } = await api.get("/whatsapp/threads");
      setThreads(data.threads || []);
    } catch (e) {
      /* silent */
    }
  }, [access?.has_access]);

  const loadMessages = useCallback(async () => {
    if (!selected) return;
    try {
      const { data } = await api.get(
        `/whatsapp/threads/${encodeURIComponent(selected)}/messages?limit=200`
      );
      setMessages(data.messages || []);
    } catch (e) {
      /* silent */
    }
  }, [selected]);

  useEffect(() => {
    loadAccess();
  }, [loadAccess]);

  useEffect(() => {
    if (!access?.has_access) return;
    loadThreads();
    pollRef.current = setInterval(() => {
      loadThreads();
      if (selected) loadMessages();
    }, 5000);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
    };
  }, [access?.has_access, loadThreads, loadMessages, selected]);

  useEffect(() => {
    loadMessages();
  }, [selected, loadMessages]);

  useEffect(() => {
    const el = listRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages.length, selected]);

  const pickFile = () => fileInputRef.current?.click();
  const onFilePicked = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (f.size > 16 * 1024 * 1024)
      return toast.error("File too large. Max 16 MB.");
    setFile(f);
    if (f.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result);
      reader.readAsDataURL(f);
    } else {
      setPreview("");
    }
  };
  const clearFile = () => {
    setFile(null);
    setPreview("");
  };

  const send = async () => {
    if (!selected) return;
    if (!text.trim() && !file) return;
    setSending(true);
    try {
      const form = new FormData();
      if (text.trim()) form.append("text", text.trim());
      if (file) form.append("file", file);
      const { data } = await api.post(
        `/whatsapp/threads/${encodeURIComponent(selected)}/messages`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setMessages((prev) => [...prev, data]);
      setText("");
      clearFile();
      loadThreads();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send");
    } finally {
      setSending(false);
    }
  };

  const startNew = () => {
    const num = newNumber.replace(/[^\d]/g, "");
    if (num.length < 8) return toast.error("Enter a valid WhatsApp number.");
    setSelected(num);
    setNewNumberOpen(false);
    setNewNumber("");
  };

  const grouped = useMemo(() => {
    const out = [];
    let lastDay = null;
    for (const m of messages) {
      const day = IST_DAY(m.created_at);
      if (day !== lastDay) {
        out.push({ _sep: true, day, id: `sep-${day}` });
        lastDay = day;
      }
      out.push(m);
    }
    return out;
  }, [messages]);

  const filteredThreads = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return threads;
    return threads.filter(
      (t) =>
        (t.customer_wa_number || "").toLowerCase().includes(q) ||
        (t.customer_name || "").toLowerCase().includes(q)
    );
  }, [threads, search]);

  // Access gate
  if (!access) return null;

  if (!access.has_access && !isOwner) {
    return (
      <div className="p-6">
        <Card className="border-slate-200 shadow-none max-w-md mx-auto">
          <CardContent className="p-6 text-center space-y-3">
            <div className="w-12 h-12 mx-auto rounded-full bg-amber-100 grid place-items-center">
              <ShieldAlert className="w-6 h-6 text-amber-700" />
            </div>
            <div className="font-heading font-bold text-lg">
              WhatsApp access needed
            </div>
            <p className="text-sm text-slate-600">
              Ask your owner to add you as a WhatsApp user.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isOwner && !access.is_configured) {
    return (
      <div className="p-6">
        <Card className="border-slate-200 shadow-none max-w-lg mx-auto">
          <CardContent className="p-6 text-center space-y-4">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 grid place-items-center">
              <MessageCircle className="w-7 h-7 text-emerald-700" />
            </div>
            <div>
              <div className="font-heading font-bold text-lg">
                Connect WhatsApp Business
              </div>
              <p className="text-sm text-slate-600 mt-1">
                Paste your Meta Cloud API credentials to start receiving
                messages from customers here.
              </p>
            </div>
            <Button
              onClick={() => setSetupOpen(true)}
              className="bg-[#25D366] hover:bg-[#128C7E]"
              data-testid="wa-open-setup"
            >
              <Settings2 className="w-4 h-4 mr-1" />
              Configure WhatsApp
            </Button>
          </CardContent>
        </Card>
        <SetupDialog
          open={setupOpen}
          onOpenChange={setSetupOpen}
          onSaved={loadAccess}
        />
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-9rem)] flex bg-[#e5ddd5] rounded-md overflow-hidden border" data-testid="wa-page">
      {/* Thread list */}
      <aside
        className={
          "w-full sm:w-[320px] bg-white flex flex-col border-r border-slate-200 " +
          (selected ? "hidden sm:flex" : "flex")
        }
      >
        <div className="bg-[#075E54] text-white px-3 py-3 flex items-center gap-2">
          <MessageCircle className="w-5 h-5" />
          <div className="font-semibold text-sm flex-1">WhatsApp</div>
          <Button
            size="sm"
            className="bg-white/15 hover:bg-white/25 text-white border-0 h-7 px-2"
            onClick={() => setNewNumberOpen(true)}
            data-testid="wa-new-thread"
          >
            <Phone className="w-3.5 h-3.5" />
          </Button>
          {isOwner && (
            <Button
              size="sm"
              className="bg-white/15 hover:bg-white/25 text-white border-0 h-7 px-2"
              onClick={() => setSetupOpen(true)}
              data-testid="wa-open-setup-inline"
            >
              <Settings2 className="w-3.5 h-3.5" />
            </Button>
          )}
        </div>
        <div className="p-2 border-b border-slate-100">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search or start"
              className="pl-7 h-8 text-sm"
              data-testid="wa-search"
            />
          </div>
        </div>
        <div className="flex-1 overflow-y-auto">
          {filteredThreads.length === 0 && (
            <div className="text-center text-slate-500 text-sm p-8">
              No conversations yet. When a customer messages your number, they
              appear here.
            </div>
          )}
          {filteredThreads.map((t) => (
            <button
              key={t.customer_wa_number}
              type="button"
              onClick={() => setSelected(t.customer_wa_number)}
              className={
                "w-full text-left px-3 py-2.5 border-b border-slate-100 flex items-center gap-2.5 " +
                (selected === t.customer_wa_number ? "bg-slate-100" : "hover:bg-slate-50")
              }
              data-testid={`wa-thread-${t.customer_wa_number}`}
            >
              <div
                className={
                  "w-10 h-10 rounded-full text-white grid place-items-center text-xs font-bold shrink-0 " +
                  avatarColor(t.customer_wa_number)
                }
              >
                {initials(t.customer_name || t.customer_wa_number)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex justify-between items-baseline gap-2">
                  <div className="text-sm font-semibold truncate">
                    {t.customer_name || `+${t.customer_wa_number}`}
                  </div>
                  <div className="mono text-[9px] text-slate-400 shrink-0">
                    {IST_TIME(t.last_at)}
                  </div>
                </div>
                <div className="text-[12px] text-slate-500 truncate flex items-center gap-1">
                  {t.last_direction === "outbound" && (
                    <StatusIcon status={t.last_status} />
                  )}
                  {t.last_kind === "text"
                    ? t.last_content || ""
                    : `[${t.last_kind}]${t.last_content ? " " + t.last_content : ""}`}
                </div>
              </div>
              {t.unread > 0 && (
                <span className="bg-[#25D366] text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] px-1 grid place-items-center">
                  {t.unread}
                </span>
              )}
            </button>
          ))}
        </div>
      </aside>

      {/* Chat pane */}
      <section
        className={
          "flex-1 flex flex-col " +
          (selected ? "flex" : "hidden sm:flex")
        }
      >
        {!selected ? (
          <div className="flex-1 grid place-items-center text-slate-500 text-sm">
            Select a conversation.
          </div>
        ) : (
          <>
            <div className="bg-[#075E54] text-white px-3 py-2.5 flex items-center gap-2">
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="sm:hidden text-white p-1"
                data-testid="wa-back"
              >
                <ArrowLeft className="w-4 h-4" />
              </button>
              <div
                className={
                  "w-9 h-9 rounded-full text-white grid place-items-center text-xs font-bold " +
                  avatarColor(selected)
                }
              >
                {initials(
                  threads.find((t) => t.customer_wa_number === selected)?.customer_name || selected
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm truncate">
                  {threads.find((t) => t.customer_wa_number === selected)?.customer_name || `+${selected}`}
                </div>
                <div className="mono text-[10px] opacity-80">
                  +{selected}
                </div>
              </div>
            </div>
            <div
              ref={listRef}
              className="flex-1 overflow-y-auto px-3 py-3"
              data-testid="wa-messages"
            >
              {grouped.map((m) => {
                if (m._sep) return <DateChip key={m.id} day={m.day} />;
                const mine = m.direction === "outbound";
                return <Bubble key={m.id} msg={m} mine={mine} />;
              })}
              {grouped.length === 0 && (
                <div className="text-center text-slate-500 text-sm mt-10">
                  No messages yet — send one to start.
                </div>
              )}
            </div>
            <div className="bg-[#f0f2f5] border-t border-slate-200 p-2">
              {file && (
                <div className="mb-2 flex items-center gap-2 bg-white rounded-md p-2 border border-slate-200">
                  {preview ? (
                    <img src={preview} alt="preview" className="w-12 h-12 object-cover rounded" />
                  ) : (
                    <div className="w-12 h-12 rounded bg-slate-100 grid place-items-center text-slate-500 text-[10px] uppercase font-bold">
                      {file.name?.split(".").pop() || "file"}
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-xs font-medium truncate">{file.name}</div>
                    <div className="mono text-[10px] text-slate-500">
                      {(file.size / (1024 * 1024)).toFixed(2)} MB
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={clearFile}
                    className="text-slate-500 hover:text-red-600 p-1"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              <div className="flex items-end gap-2">
                <button
                  type="button"
                  onClick={pickFile}
                  className="w-10 h-10 rounded-full bg-white grid place-items-center text-slate-600 hover:text-[#075E54] border border-slate-200"
                  data-testid="wa-attach"
                >
                  <Paperclip className="w-4 h-4" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={onFilePicked}
                  data-testid="wa-file-input"
                />
                <label
                  className="w-10 h-10 rounded-full bg-white grid place-items-center text-slate-600 hover:text-[#075E54] border border-slate-200 cursor-pointer"
                  data-testid="wa-camera-label"
                  aria-label="take photo"
                >
                  <Camera className="w-4 h-4" />
                  <input
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={onFilePicked}
                    data-testid="wa-camera-input"
                  />
                </label>
                <Input
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      send();
                    }
                  }}
                  placeholder="Type a message"
                  className="flex-1 bg-white"
                  data-testid="wa-input"
                />
                <Button
                  onClick={send}
                  disabled={sending || (!text.trim() && !file)}
                  className="bg-[#25D366] hover:bg-[#128C7E] w-10 h-10 p-0 rounded-full"
                  data-testid="wa-send"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </section>

      {/* New thread dialog */}
      <Dialog open={newNumberOpen} onOpenChange={setNewNumberOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="wa-newnum-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Start a conversation</DialogTitle>
            <DialogDescription>
              Enter a customer&apos;s WhatsApp number in E.164 without &quot;+&quot;. Note:
              you can only send free-form messages if they messaged you within
              the last 24 hours — otherwise Meta requires a pre-approved
              template.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={newNumber}
            onChange={(e) => setNewNumber(e.target.value)}
            placeholder="e.g. 919812340000"
            className="mono"
            data-testid="wa-newnum-input"
          />
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewNumberOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={startNew}
              className="bg-[#25D366] hover:bg-[#128C7E]"
              data-testid="wa-newnum-submit"
            >
              Open
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <SetupDialog
        open={setupOpen}
        onOpenChange={setSetupOpen}
        onSaved={loadAccess}
      />
    </div>
  );
}
